import { useState, useEffect } from 'react';
import { fetchScheduledEmails } from 'api/emailHistory/scheduledEmailsApi';
import { fetchCampaigns } from 'api/emailHistory/campaignsApi';
import { MAX_SCHEDULED_EMAILS_FETCH, MAX_CAMPAIGNS_FETCH } from 'constants/emailHistory';

/**
 * @hook useScheduledEmails
 * @description Fetches scheduled emails data with filtering
 * @param {Object} filters - Filter parameters from URL state
 * @returns {Object} Object containing scheduled emails data, stats, campaigns list, loading state, and error
 */
export function useScheduledEmails(filters) {
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({
    campaignsQueued: 0,
    upcomingEmails: 0,
    nextSend: null,
    recipientsQueued: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load campaigns list for dropdown
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const data = await fetchCampaigns({ page: 0, pageSize: MAX_CAMPAIGNS_FETCH });
        setCampaigns(data.rows || []);
      } catch (err) {
        console.error('Error loading campaigns:', err);
        // Don't set error here, just log it
      }
    };
    loadCampaigns();
  }, []);

  useEffect(() => {
    const loadScheduledEmails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch all scheduled emails (no status filter by default)
        // The API returns all scheduled emails ordered by ScheduleDate ascending
        const data = await fetchScheduledEmails({
          limit: MAX_SCHEDULED_EMAILS_FETCH,
        });

        let allEmails = data.scheduled_emails || [];

        // Apply client-side filtering
        // Filter by search (subject)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          allEmails = allEmails.filter(email => 
            email.Subject?.toLowerCase().includes(searchLower)
          );
        }

        // Filter by date range (scheduled date)
        if (filters.dateFrom || filters.dateTo) {
          allEmails = allEmails.filter(email => {
            if (!email.ScheduleDate) return false;
            
            const scheduleDate = new Date(email.ScheduleDate);
            const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
            const toDate = filters.dateTo ? new Date(filters.dateTo) : null;

            if (fromDate && scheduleDate < fromDate) return false;
            if (toDate && scheduleDate > toDate) return false;
            
            return true;
          });
        }

        // Filter by campaign (ListID)
        if (filters.campaignId) {
          allEmails = allEmails.filter(email => 
            Number(email.ListID) === Number(filters.campaignId)
          );
        }

        // Sort by scheduled date (ascending - earliest first)
        allEmails.sort((a, b) => {
          const dateA = new Date(a.ScheduleDate);
          const dateB = new Date(b.ScheduleDate);
          return dateA - dateB;
        });

        setScheduledEmails(allEmails);

        // Use stats from API response (server-side computed)
        // Fallback to defaults if stats are not available
        if (data.stats) {
          setStats({
            campaignsQueued: data.stats.campaignsQueued || 0,
            upcomingEmails: data.stats.upcomingEmails || 0,
            nextSend: data.stats.nextSend || null,
            recipientsQueued: data.stats.recipientsQueued || 0,
          });
        } else {
          // Fallback to defaults if stats are not in response
          setStats({
            campaignsQueued: 0,
            upcomingEmails: 0,
            nextSend: null,
            recipientsQueued: 0,
          });
        }
      } catch (err) {
        console.error('Error fetching scheduled emails:', err);
        setError(err.message || 'Failed to load scheduled emails');
        setScheduledEmails([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadScheduledEmails();
  }, [
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.campaignId,
    refreshTrigger, // Add refresh trigger to dependencies
  ]);

  // Expose a refetch function that can be called to refresh the data
  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    scheduledEmails,
    campaigns,
    stats,
    isLoading,
    error,
    refetch, // Expose refetch function
  };
}

